import { Fragment, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type {
  ConnectorProtocolFamily,
  ConnectorInstanceRecord,
  DocumentType,
  MappingRecord,
  PartnerRecord,
  RegisteredSchemaRecord,
} from '../types/api';

type TargetSystem = 'PMIS' | 'PCS' | 'TOS' | 'OSS internal app' | 'webhook' | 'Kafka topic' | 'SFTP export';

export interface FlowBuilderConfig extends Record<string, unknown> {
  partner: string;
  protocol: ConnectorProtocolFamily;
  endpoint: string;
  mode: string;
  messageType: DocumentType;
  schema: string;
  mapping: string;
  target: TargetSystem;
  retry: string;
  dlq: boolean;
  replay: boolean;
  timeout: string;
  idempotencyKey: string;
  testPayload: string;
  connectorInstanceId?: string;
  destinationPartnerId?: string;
  autoDeliver: boolean;
}

interface FlowBuilderStepsProps {
  step: number;
  config: FlowBuilderConfig;
  setConfig: Dispatch<SetStateAction<FlowBuilderConfig>>;
  partners?: PartnerRecord[];
  schemas?: RegisteredSchemaRecord[];
  mappings?: MappingRecord[];
  connectors?: ConnectorInstanceRecord[];
  messageTypes: DocumentType[];
  protocols: ConnectorProtocolFamily[];
  targets: TargetSystem[];
  samplePayload: string;
}

export function FlowBuilderStep({
  step,
  config,
  setConfig,
  partners = [],
  schemas = [],
  mappings = [],
  connectors = [],
  messageTypes,
  protocols,
  targets,
  samplePayload,
}: FlowBuilderStepsProps) {
  const { search } = useLocation();
  const [partnerQuery, setPartnerQuery] = useState('');
  const [includeTestPartners, setIncludeTestPartners] = useState(false);
  const visiblePartners = useMemo(() => {
    const query = partnerQuery.trim().toLocaleLowerCase();
    return partners
      .filter((partner) => partner.isActive)
      .filter((partner) => partner.id === config.partner || includeTestPartners || !/\b(e2e|test|demo|simulated)\b/i.test(`${partner.name} ${partner.code}`))
      .filter((partner) => !query || `${partner.name} ${partner.code} ${partner.environmentProfile ?? ''}`.toLocaleLowerCase().includes(query));
  }, [config.partner, includeTestPartners, partnerQuery, partners]);
  const compatibleSchemas = schemas.filter((schema) => {
    const declaredType = schema.metadata?.documentType ?? schema.metadata?.messageType ?? schema.entityType;
    return !declaredType || String(declaredType).toUpperCase() === config.messageType.toUpperCase();
  });
  const compatibleMappings = mappings.filter((mapping) => mapping.documentType === config.messageType);
  const compatibleConnectors = connectors.filter((connector) => connector.protocolFamily === config.protocol);

  if (step === 0) {
    return (
      <>
        <PanelIntro title="Step 1: Source" detail="Choose partner, protocol, endpoint/topic/folder, and synchronous or asynchronous processing." />
        {!partners.length ? (
          <SetupCallout
            title="Create a partner first"
            detail="An integration needs a partner before messages can be accepted or delivered."
            to="/config/partners"
            label="Open partners"
            search={search}
          />
        ) : null}
        <div className="form-grid">
          <label className="full-width">
            <span>Find partner</span>
            <input type="search" value={partnerQuery} onChange={(event) => setPartnerQuery(event.target.value)} placeholder="Search by partner name, code, or environment" />
          </label>
          <label>
            <span>Partner</span>
            <select value={config.partner} onChange={(event) => setConfig((current) => ({ ...current, partner: event.target.value }))}>
              <option value="">Select partner</option>
              {visiblePartners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name} ({partner.code}){partner.environmentProfile ? ` · ${partner.environmentProfile}` : ''}</option>)}
            </select>
          </label>
          <SelectField label="Protocol" value={config.protocol} options={protocols} onChange={(protocol) => setConfig((current) => ({ ...current, protocol: protocol as ConnectorProtocolFamily, connectorInstanceId: undefined }))} />
          <label>
            <span>{endpointGuidance(config.protocol).label}</span>
            <input value={config.endpoint} placeholder={endpointGuidance(config.protocol).placeholder} onChange={(event) => setConfig((current) => ({ ...current, endpoint: event.target.value }))} />
            <small className="field-hint">{endpointGuidance(config.protocol).hint}</small>
          </label>
          <SelectField label="Mode" value={config.mode} options={['Sync', 'Async']} onChange={(mode) => setConfig((current) => ({ ...current, mode }))} />
          <label className="inline-check full-width">
            <input type="checkbox" checked={includeTestPartners} onChange={(event) => setIncludeTestPartners(event.target.checked)} /> Include test and demo partners
          </label>
        </div>
      </>
    );
  }

  if (step === 1) {
    return (
      <>
        <PanelIntro title="Step 2: Message" detail="Choose the message type and attach the active schema that describes the payload." />
        {!schemas.length ? (
          <SetupCallout
            title="No active schema available"
            detail="Create or activate a schema from a real sample payload before publishing this integration."
            to="/schemas"
            label="Open schemas"
            search={search}
          />
        ) : null}
        <div className="form-grid">
          <SelectField label="Message type" value={config.messageType} options={messageTypes} onChange={(messageType) => setConfig((current) => ({ ...current, messageType: messageType as DocumentType, schema: '', mapping: '' }))} />
          <label>
            <span>Schema</span>
            <select value={config.schema} onChange={(event) => setConfig((current) => ({ ...current, schema: event.target.value }))}>
              <option value="">Select active schema</option>
              {compatibleSchemas.map((schema) => <option key={schema.id} value={`${schema.name}:${schema.version}`}>{schema.name}:{schema.version}</option>)}
            </select>
          </label>
          <label className="full-width">
            <span>Test payload</span>
            <textarea
              rows={8}
              value={config.testPayload}
              onChange={(event) => setConfig((current) => ({ ...current, testPayload: event.target.value }))}
              placeholder="Paste a real test payload when validating this flow."
            />
          </label>
        </div>
      </>
    );
  }

  if (step === 2) {
    return (
      <>
        <PanelIntro title="Step 3: Mapping" detail="Choose the active transformation rules for this message." />
        {!mappings.length ? (
          <SetupCallout
            title="No active mapping available"
            detail="Create and activate a mapping so the source message can be transformed consistently."
            to="/mapping-designer"
            label="Open mapping designer"
            search={search}
          />
        ) : null}
        <label>
          <span>Mapping version</span>
          <select value={config.mapping} onChange={(event) => setConfig((current) => ({ ...current, mapping: event.target.value }))}>
            <option value="">Select active mapping version</option>
            {/* Bug 3/4 fix: only use activeVersionId — never fall back to draft version IDs */}
            {compatibleMappings.map((mapping) => (
              <option key={mapping.id} value={mapping.activeVersionId ?? ''} disabled={!mapping.activeVersionId}>
                {mapping.name} - {mapping.version}{!mapping.activeVersionId ? ' (no active version)' : ''}
              </option>
            ))}
          </select>
        </label>
      </>
    );
  }

  if (step === 3) {
    return (
      <>
        <PanelIntro title="Step 4: Delivery" detail="Choose where the message should go and attach the delivery endpoint used by active integrations." />
        <ChoiceGrid values={targets} selected={[config.target]} onToggle={(target) => setConfig((current) => ({ ...current, target: target as TargetSystem }))} single />
        {!connectors.length ? (
          <SetupCallout
            title="No active delivery endpoint available"
            detail="Create a delivery channel when this integration must send messages automatically."
            to="/connectors"
            label="Open delivery channels"
            search={search}
          />
        ) : null}
        <div className="connector-select-block">
          <label>
            <span>Delivery endpoint</span>
            <select value={config.connectorInstanceId ?? ''} onChange={(event) => setConfig((current) => ({ ...current, connectorInstanceId: event.target.value }))}>
              <option value="">No delivery endpoint selected</option>
              {compatibleConnectors.map((connector) => <option key={connector.id} value={connector.id}>{connector.name} · {connector.protocolFamily}</option>)}
            </select>
          </label>
          <div className="field-help-actions">
            <span>Delivery endpoints are configured connector instances used by active integrations.</span>
            <Link to={{ pathname: '/connectors', search }}>Manage delivery channels</Link>
          </div>
        </div>
        <label>
          <span>Destination partner</span>
          <select value={config.destinationPartnerId ?? ''} onChange={(event) => setConfig((current) => ({ ...current, destinationPartnerId: event.target.value }))}>
            <option value="">No destination partner selected</option>
            {partners.map((partner) => <option key={partner.id} value={partner.id}>{partner.name} ({partner.code})</option>)}
          </select>
        </label>
        <label className="inline-check">
          <input type="checkbox" checked={config.autoDeliver} onChange={(event) => setConfig((current) => ({ ...current, autoDeliver: event.target.checked }))} /> Deliver automatically
        </label>
      </>
    );
  }

  if (step === 4) {
    return (
      <>
        <PanelIntro title="Step 5: Runtime policy" detail="Configure how the integration behaves when delivery is slow, duplicated, or failing." />
        <div className="form-grid">
          <NumberTextField label="Maximum attempts" value={config.retry} min={1} max={20} suffix="attempts" onChange={(retry) => setConfig((current) => ({ ...current, retry }))} />
          <NumberTextField label="Timeout" value={config.timeout} min={1} max={300} suffix="seconds" onChange={(timeout) => setConfig((current) => ({ ...current, timeout }))} />
          <SelectField label="Idempotency key" value={config.idempotencyKey} options={['correlationId', 'referenceId', 'partnerId + referenceId']} onChange={(idempotencyKey) => setConfig((current) => ({ ...current, idempotencyKey }))} />
          <label className="inline-check">
            <input type="checkbox" checked={config.dlq} onChange={(event) => setConfig((current) => ({ ...current, dlq: event.target.checked }))} /> Send failed messages to recovery
          </label>
          <label className="inline-check">
            <input type="checkbox" checked={config.replay} onChange={(event) => setConfig((current) => ({ ...current, replay: event.target.checked }))} /> Allow replay after a fix
          </label>
        </div>
      </>
    );
  }

  return (
    <>
      <PanelIntro title="Step 6: Publish" detail="Review the setup and publish when partner, message, mapping, delivery, and recovery rules are ready." />
      <FlowBuilderSummary config={config} partners={partners} schemas={schemas} mappings={mappings} connectors={connectors} />
    </>
  );
}

function SetupCallout({ title, detail, to, label, search }: { title: string; detail: string; to: string; label: string; search: string }) {
  return (
    <div className="setup-callout">
      <div>
        <strong>{title}</strong>
        <span>{detail}</span>
      </div>
      <Link to={{ pathname: to, search }}>{label}</Link>
    </div>
  );
}

function PanelIntro({ title, detail }: { title: string; detail: string }) {
  return <div className="panel-header"><div><h2>{title}</h2><p>{detail}</p></div></div>;
}

function ChoiceGrid({ values, selected, onToggle, single = false }: { values: string[]; selected: string[]; onToggle: (value: string) => void; single?: boolean }) {
  return <div className="choice-grid">{values.map((value) => <button aria-pressed={selected.includes(value)} className={`choice-tile${selected.includes(value) ? ' selected' : ''}`} type="button" key={value} onClick={() => onToggle(value)}><strong>{formatLabel(value)}</strong><span>{single ? 'Select this option' : selected.includes(value) ? 'Selected' : 'Add to subscription'}</span></button>)}</div>;
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label><span>{label}</span><input value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function NumberTextField({ label, value, min, max, suffix, onChange }: { label: string; value: string; min: number; max: number; suffix: string; onChange: (value: string) => void }) {
  return <label><span>{label}</span><div className="input-with-suffix"><input type="number" min={min} max={max} value={value} onChange={(event) => onChange(event.target.value)} /><span>{suffix}</span></div></label>;
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: readonly string[]; onChange: (value: string) => void }) {
  return <label><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option key={option} value={option}>{formatLabel(option)}</option>)}</select></label>;
}

export function FlowBuilderSummary({ config, partners = [], schemas = [], mappings = [], connectors = [] }: Pick<FlowBuilderStepsProps, 'config' | 'partners' | 'schemas' | 'mappings' | 'connectors'>) {
  const partner = partners.find((item) => item.id === config.partner);
  const mapping = mappings.find((item) => item.activeVersionId === config.mapping);
  const connector = connectors.find((item) => item.id === config.connectorInstanceId);
  const schema = schemas.find((item) => `${item.name}:${item.version}` === config.schema);
  const rows = [
    ['Partner', partner ? `${partner.name} (${partner.code})` : 'Not selected'],
    ['Source', `${formatLabel(config.protocol)} · ${config.endpoint || 'Endpoint not set'} · ${config.mode}`],
    ['Message', `${formatLabel(config.messageType)} · ${schema ? `${schema.name}:${schema.version}` : 'Schema not selected'}`],
    ['Mapping', mapping?.name ?? 'Not selected'],
    ['Destination', `${formatLabel(config.target)}${connector ? ` · ${connector.name}` : ''}`],
    ['Runtime', `${config.retry || '—'} attempts · ${config.timeout || '—'} seconds · ${formatLabel(config.idempotencyKey || 'Not set')}`],
    ['Recovery', config.dlq ? `Recovery enabled${config.replay ? ' with replay' : ''}` : 'Recovery disabled'],
    ['Automatic delivery', config.autoDeliver ? 'Enabled' : 'Disabled'],
  ];
  return <><dl className="detail-list builder-summary">{rows.map(([label, entry]) => <Fragment key={label}><dt>{label}</dt><dd>{entry}</dd></Fragment>)}</dl><details className="payload-summary"><summary>View test payload</summary><pre>{config.testPayload}</pre></details></>;
}

function endpointGuidance(protocol: ConnectorProtocolFamily): { label: string; placeholder: string; hint: string } {
  const guidance = {
    REST: { label: 'REST endpoint URL', placeholder: 'https://partner.example/api/messages', hint: 'Use a complete HTTPS URL.' },
    WEBHOOK: { label: 'Webhook URL', placeholder: 'https://partner.example/webhooks/inbound', hint: 'The URL invoked for each message.' },
    SFTP: { label: 'SFTP folder', placeholder: '/incoming/manifests', hint: 'Absolute remote folder path.' },
    KAFKA: { label: 'Kafka topic', placeholder: 'partner.cargo.inbound', hint: 'Existing topic name, without broker address.' },
    SOAP: { label: 'SOAP service URL', placeholder: 'https://partner.example/services/Cargo', hint: 'Service endpoint URL; configure credentials separately.' },
    MANUAL: { label: 'Intake label', placeholder: 'Operations manual upload', hint: 'Human-readable source name.' },
  } satisfies Record<ConnectorProtocolFamily, { label: string; placeholder: string; hint: string }>;
  return guidance[protocol];
}

function formatLabel(value: string): string {
  return value.replace(/[_-]/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}
